import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import { apiCreateNotification, apiGetAllNotifications, apiDeleteNotification, apiUpdateNotification } from '../../api';

// --- MỚI: TÙY CHỈNH EXTENSION IMAGE ĐỂ HỖ TRỢ RESIZE VÀ FLOAT ---
const ResizableImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                renderHTML: attributes => ({
                    width: attributes.width,
                }),
            },
            'data-float': {
                default: null,
                renderHTML: attributes => ({
                    'data-float': attributes['data-float'],
                }),
            },
        };
    },
});


// --- BIỂU TƯỢNG SVG CHO THANH CÔNG CỤ (ĐÃ CẬP NHẬT) ---
const Icon = ({ name }) => {
    // Các icon được thay thế bằng ảnh từ URL
    const imageIcons = {
        bold: 'https://img.icons8.com/metro/26/bold.png',
        italic: 'https://img.icons8.com/metro/26/italic.png',
        underline: 'https://img.icons8.com/metro/26/underline.png',
        link: 'https://img.icons8.com/metro/26/link.png',
    };

    if (imageIcons[name]) {
        return <img src={imageIcons[name]} alt={name} className="w-5 h-5" />;
    }

    // Các icon còn lại sử dụng SVG
    const svgIcons = {
        image: {
            path: '<path d="M 4 5 C 2.895 5 2 5.895 2 7 L 2 23 C 2 24.105 2.895 25 4 25 L 26 25 C 27.105 25 28 24.105 28 23 L 28 7 C 28 5.895 27.105 5 26 5 L 4 5 z M 23 8 C 24.105 8 25 8.895 25 10 C 25 11.105 24.105 12 23 12 C 21.895 12 21 11.105 21 10 C 21 8.895 21.895 8 23 8 z M 9 12.001953 C 9.61925 12.001953 10.238437 12.238437 10.710938 12.710938 L 13.972656 15.972656 L 15 17 L 16.15625 18.15625 C 16.57825 18.57825 17.259641 18.574344 17.681641 18.152344 C 18.104641 17.730344 18.104641 17.044094 17.681641 16.621094 L 16.529297 15.470703 L 17.289062 14.710938 C 18.234063 13.765937 19.765937 13.765937 20.710938 14.710938 L 25 19 L 25 22 L 5 22 L 5 15 L 7.2890625 12.710938 C 7.7615625 12.238437 8.38075 12.001953 9 12.001953 z"></path>',
            viewBox: '0 0 30 30',
            isFilled: true
        },
        alignLeft: { path: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 6h18M3 12h12M3 18h18" />' },
        alignCenter: { path: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 6h18M6 12h12M3 18h18" />' },
        alignRight: { path: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 6h18M9 12h12M3 18h18" />' },
        floatLeft: { path: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 6h9m-9 4h9m-9 4h9m-9 4h9M3 6h5v5H3z" />' },
        floatRight: { path: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 6h9m-9 4h9m-9 4h9m-9 4h9M16 6h5v5h-5z" />' },
        clearFloat: { path: '<path stroke-linecap="round" stroke-linejoin="round" d="M3 6h18M3 12h18M3 18h18" />' }
    };

    const iconData = svgIcons[name];
    if (!iconData) return null;

    return (
        <svg
            width="20" height="20"
            viewBox={iconData.viewBox || '0 0 24 24'}
            fill={iconData.isFilled ? 'currentColor' : 'none'}
            stroke={iconData.isFilled ? 'none' : 'currentColor'}
            strokeWidth={iconData.isFilled ? undefined : '2'}
            xmlns="http://www.w3.org/2000/svg"
            dangerouslySetInnerHTML={{ __html: iconData.path }}
        />
    );
};

// --- THANH CÔNG CỤ HIỆN ĐẠI ---
const MenuBar = ({ editor, onAddImageClick }) => {
    if (!editor) return null;
    
    const setLink = useCallback(() => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Nhập URL liên kết:', previousUrl);
        if (url === null) return;
        if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    return (
        <div className="border border-gray-300 rounded-t-md p-2 flex flex-wrap items-center gap-1 bg-gray-50">
            <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={`p-2 rounded ${editor.isActive('bold') ? 'bg-gray-300' : 'hover:bg-gray-200'}`}><Icon name="bold" /></button>
            <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={`p-2 rounded ${editor.isActive('italic') ? 'bg-gray-300' : 'hover:bg-gray-200'}`}><Icon name="italic" /></button>
            <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={`p-2 rounded ${editor.isActive('underline') ? 'bg-gray-300' : 'hover:bg-gray-200'}`}><Icon name="underline" /></button>
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            
            {/* Cập nhật nút chọn màu chữ */}
            <label className="relative w-9 h-9 flex items-center justify-center p-2 rounded hover:bg-gray-200 cursor-pointer">
                <img src="https://img.icons8.com/color/48/text-color.png" alt="text-color" className="w-6 h-6"/>
                <input
                    type="color"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
                    value={editor.getAttributes('textStyle').color || '#000000'}
                />
            </label>

            <button type="button" onClick={() => editor.chain().focus().toggleHighlight({ color: '#FFF3A3' }).run()} className={`p-2 rounded ${editor.isActive('highlight', { color: '#FFF3A3' }) ? 'bg-yellow-200' : 'hover:bg-gray-200'}`}>🎨</button>
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            <button type="button" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={`p-2 rounded ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-300' : 'hover:bg-gray-200'}`}><Icon name="alignLeft" /></button>
            <button type="button" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={`p-2 rounded ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-300' : 'hover:bg-gray-200'}`}><Icon name="alignCenter" /></button>
            <button type="button" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={`p-2 rounded ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-300' : 'hover:bg-gray-200'}`}><Icon name="alignRight" /></button>
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            <button type="button" onClick={onAddImageClick} className="p-2 rounded hover:bg-gray-200"><Icon name="image" /></button>
            <button type="button" onClick={setLink} className={`p-2 rounded ${editor.isActive('link') ? 'bg-gray-300' : 'hover:bg-gray-200'}`}><Icon name="link" /></button>
            
            {editor.isActive('image') && (
                <>
                    <div className="w-px h-6 bg-gray-300 mx-1"></div>
                    <button type="button" onClick={() => editor.chain().focus().updateAttributes('image', { 'data-float': 'left' }).run()} className={`p-2 rounded ${editor.isActive('image', { 'data-float': 'left' }) ? 'bg-gray-300' : 'hover:bg-gray-200'}`} title="Căn trái"><Icon name="floatLeft" /></button>
                    <button type="button" onClick={() => editor.chain().focus().updateAttributes('image', { 'data-float': 'right' }).run()} className={`p-2 rounded ${editor.isActive('image', { 'data-float': 'right' }) ? 'bg-gray-300' : 'hover:bg-gray-200'}`} title="Căn phải"><Icon name="floatRight" /></button>
                    <button type="button" onClick={() => editor.chain().focus().updateAttributes('image', { 'data-float': null, width: '100%' }).run()} className={`p-2 rounded ${!editor.getAttributes('image')['data-float'] ? 'bg-gray-300' : 'hover:bg-gray-200'}`} title="Căn giữa"><Icon name="clearFloat" /></button>
                    <div className="w-px h-6 bg-gray-300 mx-1"></div>
                    <button type="button" onClick={() => editor.chain().focus().updateAttributes('image', { width: '25%' }).run()} className={`p-2 rounded text-xs ${editor.isActive('image', { width: '25%' }) ? 'bg-gray-300' : 'hover:bg-gray-200'}`}>Nhỏ</button>
                    <button type="button" onClick={() => editor.chain().focus().updateAttributes('image', { width: '50%' }).run()} className={`p-2 rounded text-xs ${editor.isActive('image', { width: '50%' }) ? 'bg-gray-300' : 'hover:bg-gray-200'}`}>Vừa</button>
                    <button type="button" onClick={() => editor.chain().focus().updateAttributes('image', { width: '100%' }).run()} className={`p-2 rounded text-xs ${editor.isActive('image', { width: '100%' }) ? 'bg-gray-300' : 'hover:bg-gray-200'}`}>Lớn</button>
                </>
            )}

            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            {['🎉', '📢', '✅', '💡', '❗'].map(emoji => (
                <button key={emoji} type="button" onClick={() => editor.chain().focus().insertContent(emoji).run()} className="p-2 rounded hover:bg-gray-200 text-xl">{emoji}</button>
            ))}
        </div>
    );
};

// --- MODAL CHÈN ẢNH VỚI TÍNH NĂNG XEM TRƯỚC (Không đổi) ---
const ImageInsertModal = ({ isOpen, onClose, onInsert }) => {
    const [imageUrl, setImageUrl] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    const handleInsert = () => {
        if (imageUrl) {
            onInsert(imageUrl);
            setImageUrl('');
            onClose();
        }
    };
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleInsert();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
                <h3 className="text-xl font-semibold text-gray-800">Chèn hình ảnh</h3>
                <div>
                    <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700 mb-1">
                        Đường dẫn (URL) hình ảnh
                    </label>
                    <input
                        ref={inputRef}
                        id="imageUrl"
                        type="text"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="https://i.anh.moe/2024/09/23/ximnS0.png"
                    />
                </div>
                {imageUrl && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Xem trước:</label>
                        <div className="border rounded-md p-2 min-h-[100px] flex justify-center items-center bg-gray-50">
                            <img
                                src={imageUrl}
                                alt="Xem trước"
                                className="max-w-full max-h-48 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                onLoad={(e) => { e.target.style.display = 'block'; e.target.nextSibling.style.display = 'none'; }}
                            />
                            <span style={{ display: 'none' }} className="text-gray-500">URL không hợp lệ hoặc không thể tải ảnh.</span>
                        </div>
                    </div>
                )}
                <div className="flex justify-end gap-4">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                        Hủy
                    </button>
                    <button onClick={handleInsert} disabled={!imageUrl} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-300">
                        Chèn ảnh
                    </button>
                </div>
            </div>
        </div>
    );
};


// --- Component chính ---
export default function AdminNotifications() {
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState('');
    const [sender, setSender] = useState('Phòng Nhân sự');
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    
    const editor = useEditor({
        extensions: [
            StarterKit, Underline,
            ResizableImage,
            Link.configure({ openOnClick: false }),
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            TextStyle, Color,
            Placeholder.configure({ placeholder: 'Nhập nội dung thông báo ở đây...' }),
        ],
        content: '',
        editorProps: {
            attributes: {
                class: 'prose max-w-none p-4 border-b border-x border-gray-300 rounded-b-md min-h-[300px] focus:outline-none tiptap',
            },
        },
    });

    const fetchNotifications = useCallback(async () => {
        setIsLoadingList(true);
        try {
            const data = await apiGetAllNotifications();
            setNotifications(data);
        } catch (error) {
            setMessage({ type: 'error', text: `Lỗi tải danh sách: ${error.message}` });
        } finally {
            setIsLoadingList(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);
    
    const resetForm = useCallback(() => {
        setTitle('');
        setSender('Phòng Nhân sự');
        editor?.commands.clearContent();
        setEditingId(null);
        setMessage({ type: '', text: '' });
    }, [editor]);
    
    const handleStartEdit = useCallback((noti) => {
        setEditingId(noti.NotificationID);
        setTitle(noti.Title);
        setSender(noti.Sender);
        editor?.commands.setContent(noti.Content || '');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [editor]);
    
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!editor) return;
        const htmlContent = editor.getHTML();

        if (!title.trim() || editor.isEmpty) {
            setMessage({ type: 'error', text: 'Vui lòng nhập tiêu đề và nội dung.' });
            return;
        }

        setIsLoading(true);
        setMessage({ type: '', text: '' });
        try {
            let response;
            if (editingId) {
                response = await apiUpdateNotification(editingId, title, htmlContent, sender);
            } else {
                response = await apiCreateNotification(title, htmlContent, sender);
            }
            setMessage({ type: 'success', text: response.message });
            resetForm();
            fetchNotifications();
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
        }
    }, [editor, title, sender, editingId, fetchNotifications, resetForm]);
    
    const handleDelete = useCallback(async (notificationId) => {
        if (!window.confirm('Bạn có chắc chắn muốn xóa thông báo này không?')) return;
        try {
            await apiDeleteNotification(notificationId);
            setNotifications(prev => prev.map(n => 
                n.NotificationID === notificationId ? { ...n, IsActive: false } : n
            ));
            setMessage({ type: 'success', text: 'Xóa thông báo thành công.' });
        } catch (error) {
            setMessage({ type: 'error', text: `Lỗi khi xóa: ${error.message}` });
        }
    }, []);

    const handleInsertImage = useCallback((url) => {
        if (url && editor) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    }, [editor]);
    
    return (
        <>
            <style>{`
                .tiptap img {
                    max-width: 100%;
                    height: auto;
                    display: block;
                    margin-left: auto;
                    margin-right: auto;
                }
                .tiptap img[data-float="left"] {
                    float: left;
                    margin-right: 1rem;
                    margin-bottom: 0.5rem;
                }
                .tiptap img[data-float="right"] {
                    float: right;
                    margin-left: 1rem;
                    margin-bottom: 0.5rem;
                }
                .tiptap img.ProseMirror-selectednode {
                  outline: 3px solid #68CEF8;
                }
            `}</style>
        
            <ImageInsertModal
                isOpen={isImageModalOpen}
                onClose={() => setIsImageModalOpen(false)}
                onInsert={handleInsertImage}
            />

            <div className="max-w-4xl mx-auto p-6 space-y-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h1 className="text-3xl font-bold text-gray-800 mb-6">
                        {editingId ? 'Chỉnh sửa thông báo' : 'Soạn thảo thông báo mới'}
                    </h1>
                    
                    {message.text && (
                        <div className={`p-4 mb-4 rounded-md text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} role="alert">
                            {message.text}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="title" className="block text-gray-700 font-semibold mb-2">Tiêu đề</label>
                                <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                            </div>
                            <div>
                                <label htmlFor="sender" className="block text-gray-700 font-semibold mb-2">Bộ phận gửi</label>
                                <select id="sender" value={sender} onChange={(e) => setSender(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                    <option>Phòng Nhân sự</option>
                                    <option>Phòng Kế toán</option>
                                    <option>Ban Giám đốc</option>
                                    <option>Công đoàn</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">Nội dung</label>
                            <MenuBar editor={editor} onAddImageClick={() => setIsImageModalOpen(true)} />
                            <EditorContent editor={editor} />
                        </div>

                        <div className="flex items-center gap-4 pt-2">
                            <button type="submit" disabled={isLoading || !editor} className="flex-1 px-4 py-3 bg-indigo-600 text-white font-bold rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors">
                                {isLoading ? 'Đang xử lý...' : (editingId ? 'Cập nhật thông báo' : 'Gửi thông báo')}
                            </button>
                            {editingId && (
                                <button type="button" onClick={resetForm} className="px-4 py-3 bg-gray-500 text-white font-bold rounded-md hover:bg-gray-600">Hủy</button>
                            )}
                        </div>
                    </form>
                </div>
                
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Danh sách đã gửi</h2>
                    {isLoadingList ? <p>Đang tải danh sách...</p> : notifications.length === 0 ? <p>Chưa có thông báo nào được gửi.</p> : (
                        <ul className="space-y-4">
                            {notifications.map((noti) => (
                                <li key={noti.NotificationID} className={`p-4 rounded-md flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 transition-opacity ${noti.IsActive ? 'bg-gray-50' : 'bg-red-50 opacity-60'}`}>
                                    <div>
                                        <p className="text-sm font-semibold text-indigo-600">{noti.Sender}</p>
                                        <h3 className="font-bold text-gray-800">{noti.Title}</h3>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Ngày gửi: {new Date(noti.CreatedAt).toLocaleString('vi-VN')}
                                            {!noti.IsActive && <span className="ml-2 font-semibold text-red-600">(Đã xóa)</span>}
                                        </p>
                                    </div>
                                    {noti.IsActive && (
                                         <div className="flex items-center gap-2 flex-shrink-0">
                                            <button onClick={() => handleStartEdit(noti)} className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">Sửa</button>
                                            <button onClick={() => handleDelete(noti.NotificationID)} className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600">Xóa</button>
                                         </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </>
    );
}

